import { FC, ReactElement } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

import { multiStackBarChart } from "@/config";

import "./style.css";

type SingleYAxis = Exclude<ApexOptions["yaxis"], ApexOptions["yaxis"][]>;

const MultiStackBarChart: FC<{
  data?: { name: string; data: number[] }[];
  categories?: string | number[];
  colors?: string[];
}> = ({ data = [], categories = [], colors }): ReactElement => {
  const getYAxisOffset = (dataLength: number) => {
    if (dataLength === 0) return -15;
    if (dataLength === 1) return -85;
    if (dataLength === 2) return -45;
    if (dataLength === 3) return -32;
    if (dataLength === 4) return -26;
    return -22;
  };

  const maxDataLength = data.length
    ? Math.max(...data.map((series) => series.data.filter(Boolean).length))
    : 0;

  const yAxisConfig = multiStackBarChart.yaxis as SingleYAxis;

  const safeSeries = (data && data.length > 0) ? data : [{ name: "", data: [0] }];

  const safeCategories = Array.isArray(categories) ? categories : [];

  return (
    <ReactApexChart
      options={{
        ...multiStackBarChart,
        colors: colors ?? multiStackBarChart.colors,
        xaxis: {
          ...multiStackBarChart.xaxis,
          categories: safeCategories,
          labels: {
            ...multiStackBarChart.xaxis?.labels,
            formatter: function (val) {
              // Ensure categories are displayed exactly as provided without any formatting
              return String(val).split("|")[0].trim();
            },
          },
        },
        yaxis: {
          ...yAxisConfig,
          labels: {
            ...yAxisConfig?.labels,
            offsetY: getYAxisOffset(maxDataLength),
            formatter: (val) => val.toString(),
          },
        },
        tooltip: {
          ...multiStackBarChart.tooltip,
          x: {
            formatter: (val) => String(val).split("|")[0].trim(),
          },
          y: {
            formatter: (value) => value.toString(),
            title: {
              formatter: (seriesName) => seriesName,
            },
          },
          custom: undefined,
          shared: true,
          intersect: false,
          followCursor: true,
        },
        dataLabels: {
          ...multiStackBarChart.dataLabels,
          formatter: (value) => value.toString(),
          enabled: false, // Try disabling data labels completely
        },
        plotOptions: {
          ...multiStackBarChart.plotOptions,
          bar: {
            ...multiStackBarChart?.plotOptions?.bar,
          },
        },
        // Prevent percentage calculations
        chart: {
          ...multiStackBarChart.chart,
          stacked: true,
          stackType: "normal",
        },
      }}
      series={safeSeries}
      type="bar"
      width="100%"
      height={310}
    />
  );
};

export default MultiStackBarChart;
